import { useState } from 'react';

import { IconFile, IconFolder, IconFolderOpen, IconLock } from '@tabler/icons-react';

import { ActionIcon, Box, Center, Collapse, Loader, MantineStyleProp, Tooltip, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { AzureDevOpsFileTreeItemDto, ProjectDto } from '../../api/models';
import { customInstance } from '../../../orvalCustomAxiosInstance';

const LockedIcon = ({ icon: Icon }: { icon: typeof IconFile }) => (
    <ActionIcon variant="subtle" size="md" color="gray" style={{ position: 'relative' }}>
        <Icon />
        <IconLock size={12} stroke={3} style={{ position: 'absolute', bottom: 1, right: 1 }} />
    </ActionIcon>
);

interface DevOpsFileTreeProps {
    project: ProjectDto;
    onFileSelected: (filePath: string) => void;
    documentStyle?: MantineStyleProp;
}

interface FileGroup {
    folder: string;
    files: AzureDevOpsFileTreeItemDto[];
}

function groupByFolder(files: AzureDevOpsFileTreeItemDto[]): { rootFiles: AzureDevOpsFileTreeItemDto[]; folders: FileGroup[] } {
    const folderMap = new Map<string, AzureDevOpsFileTreeItemDto[]>();
    const rootFiles: AzureDevOpsFileTreeItemDto[] = [];

    for (const file of files) {
        const lastSlash = file.path.lastIndexOf('/');
        if (lastSlash <= 0) {
            rootFiles.push(file);
        } else {
            const folder = file.path.substring(1, lastSlash);
            if (!folderMap.has(folder)) {
                folderMap.set(folder, []);
            }
            folderMap.get(folder)!.push(file);
        }
    }

    const folders = Array.from(folderMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([folder, folderFiles]) => ({
            folder,
            files: folderFiles.sort((a, b) => a.name.localeCompare(b.name))
        }));

    rootFiles.sort((a, b) => a.name.localeCompare(b.name));

    return { rootFiles, folders };
}

interface FolderGroupProps {
    group: FileGroup;
    onFileSelected: (filePath: string) => void;
    itemStyle: MantineStyleProp;
    opened: boolean;
    onToggle: () => void;
}

const FolderGroup = ({ group, onFileSelected, itemStyle, opened, onToggle }: FolderGroupProps) => {
    const lastSegment = group.folder.split('/').pop() ?? group.folder;

    return (
        <div>
            <Tooltip label={group.folder}>
                <UnstyledButton
                    style={itemStyle}
                    onClick={onToggle}
                >
                    <Box style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, flexShrink: 0 }}>
                        <LockedIcon icon={opened ? IconFolderOpen : IconFolder} />
                    </Box>
                    <Box ml="md" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lastSegment}</Box>
                </UnstyledButton>
            </Tooltip>
            <Collapse in={opened}>
                {group.files.map(file => (
                    <FileItem key={file.path} file={file} onFileSelected={onFileSelected} itemStyle={itemStyle} nested />
                ))}
            </Collapse>
        </div>
    );
};

interface FileItemProps {
    file: AzureDevOpsFileTreeItemDto;
    onFileSelected: (filePath: string) => void;
    itemStyle: MantineStyleProp;
    nested?: boolean;
}

const FileItem = ({ file, onFileSelected, itemStyle, nested }: FileItemProps) => (
    <Tooltip label={file.path}>
        <UnstyledButton
            style={nested ? { ...itemStyle as object, paddingLeft: `calc(var(--mantine-spacing-md) + 20px)` } : itemStyle}
            onClick={() => onFileSelected(file.path)}
        >
            <Box style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, flexShrink: 0 }}>
                <LockedIcon icon={IconFile} />
            </Box>
            <Box ml="md" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Box>
        </UnstyledButton>
    </Tooltip>
);

const DevOpsFileTree = ({ project, onFileSelected, documentStyle }: DevOpsFileTreeProps) => {
    const hasDevOps = !!(project.devOpsRepoId && project.hasDevOpsPat);
    const [openFolder, setOpenFolder] = useState<string | null>(null);

    const { data: files, isLoading, error } = useQuery({
        queryKey: [`/api/AzureDevOps/project/${project.id}/tree`],
        queryFn: ({ signal }) =>
            customInstance<AzureDevOpsFileTreeItemDto[]>({
                url: `/api/AzureDevOps/project/${project.id}/tree`,
                method: 'GET',
                signal
            }),
        enabled: hasDevOps
    });

    const itemStyle: MantineStyleProp = documentStyle ? { ...documentStyle as object } : {
        fontWeight: '500',
        width: 'calc(100% - var(--mantine-spacing-xl))',
        textDecoration: 'none',
        marginLeft: 'var(--mantine-spacing-xl)',
        borderLeft: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        display: 'flex',
        alignItems: 'center',
        padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
        color: 'var(--mantine-color-text)',
        fontSize: 'var(--mantine-font-size-sm)',
        overflow: 'hidden'
    };

    if (isLoading) {
        return (
            <Center py="xs" ml="xl">
                <Loader size="xs" />
            </Center>
        );
    }

    if (error || !files || files.length === 0) {
        return null;
    }

    const { rootFiles, folders } = groupByFolder(files);

    return (
        <>
            <Box fz="xs" c="dimmed" ml="xl" pl="md" mt={4} mb={2}>
                Repository Files
            </Box>
            {rootFiles.map(file => (
                <FileItem key={file.path} file={file} onFileSelected={(path) => { setOpenFolder(null); onFileSelected(path); }} itemStyle={itemStyle} />
            ))}
            {folders.map(group => (
                <FolderGroup
                    key={group.folder}
                    group={group}
                    onFileSelected={onFileSelected}
                    itemStyle={itemStyle}
                    opened={openFolder === group.folder}
                    onToggle={() => setOpenFolder(prev => prev === group.folder ? null : group.folder)}
                />
            ))}
        </>
    );
};

export default DevOpsFileTree;
